import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  makeStyles,
  Button,
  Input,
  Spinner,
  AvatarGroup,
  Toolbar,
  ToolbarButton,
  Badge,
  TabList,
  Tab,
} from "@fluentui/react-components";
import {
  ArrowClockwiseRegular,
  Edit24Regular,
  Note24Regular,
  Mail24Regular,
  Desktop24Regular,
} from "@fluentui/react-icons";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

// Components
import "../../../styles/page.css";
import { ErrorMessagesBar } from "../../../shared/components/ErrorMessagesBar";
import { useNotify } from "../../../shared/components/SuperToast";
import CiSelector from "../../ci/components/CiSelector";
import { useAuth } from "../../../provider/authProvider";
import { UserAvatarGroupItem } from "../../users/components/UserAvatar";

// Business logic
import TicketEditorProperties from "./TicketEditorProperties";
import TicketEditorConversations from "./TicketEditorConversations";
import { matrix } from "@shared/data";
import { ticketSchema } from "@shared/schemas";
import { useItemQuery } from "../../../shared/hooks/useItemQuery";
import { useItemValidation } from "../../../shared/hooks/useItemValidation";
import { useCreateTicket, useUpdateTicket } from "../hooks/useTicketMutations";
import { readCis } from "../../ci/services/ciApi";
import { readCategories } from "../../categories/services/categoryApi";
import { readSites } from "../../sites/services/siteApi";
import { readTicket, ReadTicketSources, ReadTicketStatuses } from "../../tickets/services/ticketApi";
import { readTicketUpdates } from "../../ticketUpdates/services/ticketUpdateApi";
import { readUsers } from "../../users/services/userApi";
import { handlePendingFiles } from "../../files/services/pendingFiles";
import CisBar from "../../ci/components/CisBar";

const impactOptions = [
  { id: "low", label: "Low", color: "#107C10" },
  { id: "medium", label: "Medium", color: "#FF8C00" },
  { id: "high", label: "High", color: "#D13438" },
];
const priorityOptions = [
  { id: "low", label: "Low", color: "#107C10" },
  { id: "medium", label: "Medium", color: "#FF8C00" },
  { id: "high", label: "High", color: "#FFB900" },
  { id: "critical", label: "Critical", color: "#D13438" },
];
const autoOpenAccordionItems = ["additionalDetails"];
const useStyles = makeStyles({
  field: {
    display: "grid",
    fontSize: "13px",
    gridRowGap: "0px",
    margin: "2px 0",
    size: "small",
  },
});

function TicketEditor({ newTicket }) {
  const { t } = useTranslation();
  const { publicId } = useParams();
  const [CurrentTicket, setCurrentTicket] = useState(null);
  const [associatedCis, setAssociatedCis] = useState([]);

  const [mailProperties, setMailProperties] = useState({});
  //const [editorValue, setEditorValue] = useState("");
  const [openItems, setOpenItems] = useState([]);
  const [userToggledAccordions, setUserToggledAccordions] = useState(false);
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const [isCiSelectorOpen, setIsCiSelectorOpen] = useState(false);
  const [updateLanguage, setUpdateLanguage] = useState("");
  const [showEditor, setShowEditor] = useState(false);
  const [showMailEditor, setShowMailEditor] = useState(false);
  const styles = useStyles();
  const navigate = useNavigate();
  const { notify } = useNotify();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const accordionRef = useRef(null);
  const conversationRef = useRef(null);
  const [hasScrollbar, setHasScrollbar] = useState(false);
  const attachmentsRef = useRef(null);

  // Validation
  const [validationErrors, setValidationErrors] = useState({});
  const validateTicket = useItemValidation(ticketSchema.$defs.ticket);

  // Errors
  const [errors, setErrors] = useState([]);

  // Queries
  const {
    data: ticketData,
    isLoading,
    error: ticketError,
  } = useItemQuery(["ticket", publicId], () => readTicket(publicId), !newTicket, "TicketEditor");

  const { data: agentsData, error: agentsError } = useItemQuery(
    ["agents"],
    () => readUsers({ is_agent: 1 }),
    true,
    "TicketEditor",
  );
  const { data: categoriesData, error: categoriesError } = useItemQuery(
    ["Categories"],
    () => readCategories({ level: 0 }),
    true,
    "TicketEditor",
  );
  const { data: CiData, error: ciError } = useQuery({
    queryKey: ["Cis", { ticket_id: CurrentTicket?.id }],
    queryFn: () => readCis({ ticket_id: CurrentTicket?.id }),
    enabled: !!CurrentTicket?.id,
    staleTime: Infinity,
    gcTime: 1 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const { data: sitesData, error: sitesError } = useItemQuery(["Sites"], readSites, true, "TicketEditor");
  const { data: subcategoriesData, error: subcategoriesError } = useItemQuery(
    ["Subcategories", CurrentTicket?.category_id],
    () => readCategories({ level: 1, parent_id: CurrentTicket?.category_id }),
    CurrentTicket?.category_id != null,
    "TicketEditor",
  );
  const { data: itemsData, error: itemsError } = useItemQuery(
    ["Items", CurrentTicket?.subcategory_id],
    () => readCategories({ level: 2, parent_id: CurrentTicket?.subcategory_id }),
    CurrentTicket?.subcategory_id != null,
    "TicketEditor",
  );
  const { data: ticketSourcesData, error: ticketSourcesError } = useItemQuery(
    ["TicketSources"],
    ReadTicketSources,
    true,
    "TicketEditor",
  );
  const { data: ticketStatusesData, error: ticketStatusesError } = useItemQuery(
    ["TicketStatuses"],
    ReadTicketStatuses,
    true,
    "TicketEditor",
  );
  const { data: usersData, error: usersError } = useItemQuery(["users"], readUsers, true, "TicketEditor");
  const {
    data: ticketUpdatesData,
    error: ticketUpdatesError,
    refetch: refetchTicketUpdates,
  } = useItemQuery(
    ["TicketUpdates", CurrentTicket?.id],
    () => readTicketUpdates({ ticket_id: CurrentTicket?.id }),
    CurrentTicket?.id != null,
    true,
  );

  // Mutations
  const {
    mutate: createTicketMutate,
    error: createTicketError,
    isPending: isCreating,
  } = useCreateTicket(
    ["tickets"],
    (data) => {
      const pendingAttachments = attachmentsRef.current?.getPendingAttachments() || [];
      const pendingDeletions = attachmentsRef.current?.getPendingDeletions() || [];
      handlePendingFiles({ pendingAttachments, pendingDeletions, ticketUpdate: data.update, queryClient });
      attachmentsRef.current?.clearPending();

      notify(t("item.messages.created", { name: `${data.type}-${data.id}` }), "success");
      handleClose();
    },
    null,
    true,
  );
  const {
    mutate: updateTicketMutate,
    error: updateTicketError,
    isPending: isUpdating,
  } = useUpdateTicket(
    ["tickets"],
    (data) => {
      notify(t("item.messages.updated", { name: `${data.type}-${data.id}` }), "success");
      handleClose();
    },
    null,
    true,
  );

  // Effects
  useEffect(() => {
    const newErrors = [];
    if (agentsError) {
      newErrors.push({ message: agentsError?.message || t("generic.error"), type: "error", details: agentsError });
    }
    if (categoriesError) {
      newErrors.push({
        message: categoriesError?.message || t("generic.error"),
        type: "error",
        details: categoriesError,
      });
    }
    if (ciError) {
      newErrors.push({
        message: ciError?.message || t("generic.error"),
        type: "error",
        details: ciError,
      });
    }
    if (sitesError) {
      newErrors.push({ message: sitesError?.message || t("generic.error"), type: "error", details: sitesError });
    }
    if (subcategoriesError) {
      newErrors.push({
        message: subcategoriesError?.message || t("generic.error"),
        type: "error",
        details: subcategoriesError,
      });
    }
    if (itemsError) {
      newErrors.push({ message: itemsError?.message || t("generic.error"), type: "error", details: itemsError });
    }
    if (ticketSourcesError) {
      newErrors.push({
        message: ticketSourcesError?.message || t("generic.error"),
        type: "error",
        details: ticketSourcesError,
      });
    }
    if (ticketStatusesError) {
      newErrors.push({
        message: ticketStatusesError?.message || t("generic.error"),
        type: "error",
        details: ticketStatusesError,
      });
    }
    if (usersError) {
      newErrors.push({ message: usersError?.message || t("generic.error"), type: "error", details: usersError });
    }
    if (ticketError) {
      newErrors.push({ message: ticketError?.message || t("generic.error"), type: "error", details: ticketError });
    }
    if (ticketUpdatesError) {
      newErrors.push({
        message: ticketUpdatesError?.message || t("generic.error"),
        type: "error",
        details: ticketUpdatesError,
      });
    }
    if (createTicketError) {
      newErrors.push({
        message: createTicketError?.message || t("generic.error"),
        type: "error",
        details: createTicketError,
      });
    }
    if (updateTicketError) {
      newErrors.push({
        message: updateTicketError?.message || t("generic.error"),
        type: "error",
        details: updateTicketError,
      });
    }
    if (newErrors.length > 0) {
      setErrors((prevErrors) => [...prevErrors, ...newErrors]);
    }
  }, [
    agentsError,
    ciError,
    categoriesError,
    sitesError,
    subcategoriesError,
    itemsError,
    ticketSourcesError,
    ticketStatusesError,
    usersError,
    ticketError,
    ticketUpdatesError,
    createTicketError,
    updateTicketError,
    t,
  ]);

  useEffect(() => {
    if (newTicket) {
      const newTicket = { type: "INC", status_id: 1, source_id: 1, impact: "low", requestedby_id: currentUser?.id };
      setCurrentTicket(newTicket);
      setValidationErrors(validateTicket(newTicket));
      setIsTitleEditing(true);
    } else if (ticketData) {
      setCurrentTicket(ticketData);
      const errors = validateTicket(ticketData);
      setValidationErrors(errors);
      setIsTitleEditing(!ticketData?.title);
    }
  }, [publicId, ticketData]);

  useEffect(() => {
    if (CiData && CiData.length > 0) {
      setAssociatedCis(CiData);
    }
  }, [CiData]);

  // Handlers
  const handleClose = () => {
    queryClient.removeQueries({ queryKey: ["ticket", publicId] });
    setCurrentTicket(null);
    setValidationErrors({});
    setErrors([]);
    navigate("/tickets");
  };

  const handleOnEmailReply = (ticketUpdate, action = "reply") => {
    const replyBody = `\n\n--- ${t("ticket.originalMessage", { defaultValue: "Original message" })} ---\n${ticketUpdate.content}`;
    const ticketId = `[#${CurrentTicket?.type}-${CurrentTicket?.id}]`;
    const prefix = action === "forward" ? "Fwd" : "Re";
    setMailProperties({
      from: action === "reply" || action === "replyAll" ? ticketUpdate.to_address : "",
      to: action === "reply" || action === "replyAll" ? ticketUpdate.from_address : "",
      cc: action === "replyAll" ? ticketUpdate.cc_address : "",
      subject: `${prefix}: ${ticketId} ${ticketUpdate.subject}`,
      body: replyBody,
    });
    //setEditorValue(replyBody);
    setShowMailEditor(true);
    setShowEditor(false);
  };

  const handleOnCiSelect = (ci) => {
    if (!ci) return;
    if (associatedCis.find((c) => c.public_id === ci.public_id)) return;
    setAssociatedCis((prev) => [...prev, ci]);
  };

  const handleRemoveCi = (ciPublicId) => {
    setAssociatedCis((prev) => prev.filter((ci) => ci.public_id !== ciPublicId));
  };

  const updateField = (field, value) => {
    const updated = { ...CurrentTicket, [field]: typeof value === "string" && value === "" ? null : value };
    if (field === "category_id") {
      updated.subcategory_id = null;
    }
    if (updated.impact && updated.urgency) {
      updated.priority =
        matrix.find((i) => i.impact === updated.impact && i.urgency === updated.urgency)?.priority || "low";
    }
    setCurrentTicket(updated);
    setValidationErrors(validateTicket(updated));
  };

  const attemptAutoExpand = () => {
    if (!accordionRef.current || userToggledAccordions) return;
    setOpenItems((prev) => Array.from(new Set([...prev, ...autoOpenAccordionItems])));
  };

  useEffect(() => {
    const handleResize = () => attemptAutoExpand();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [userToggledAccordions]);

  useEffect(() => {
    attemptAutoExpand();
  }, [CurrentTicket, ticketStatusesData, ticketSourcesData, usersData, sitesData]);

  useEffect(() => {
    const checkScrollbar = () => {
      if (conversationRef.current) {
        const hasScroll = conversationRef.current.scrollHeight > conversationRef.current.clientHeight;
        setHasScrollbar(hasScroll);
      }
    };
    checkScrollbar();
    const resizeObserver = new ResizeObserver(checkScrollbar);
    if (conversationRef.current) {
      resizeObserver.observe(conversationRef.current);
    }
    return () => resizeObserver.disconnect();
  }, [ticketUpdatesData, CurrentTicket]);

  useEffect(() => {
    if (!accordionRef.current || userToggledAccordions) return;
    const hasAllDesiredOpen = autoOpenAccordionItems.every((item) => openItems.includes(item));
    if (!hasAllDesiredOpen) return;
    const container = accordionRef.current;
    const hasScrollbar = container.scrollHeight - container.clientHeight > 1;
    if (hasScrollbar) {
      setOpenItems((prev) => prev.filter((item) => !autoOpenAccordionItems.includes(item)));
    }
  }, [openItems, userToggledAccordions]);

  if (isLoading) return <Spinner label={t("nounPhrases.dataLoading")} labelPosition="below" />;
  else
    return (
      <div className="page" style={{ overflowY: "hidden" }}>
        <header style={{ display: "flex", alignItems: "center", width: "100%" }}>
          <span
            style={{ display: "inline-flex", flex: 1, minWidth: 0, alignItems: "center", gap: "12px", width: "100%" }}
          >
            <AvatarGroup layout="stack" key={`${CurrentTicket?.requestedby_id}-${CurrentTicket?.requestedfor_id}`}>
              <UserAvatarGroupItem
                user={{
                  id: CurrentTicket?.requestedby_id,
                  name: CurrentTicket?.requested_by,
                  public_photo_id: CurrentTicket?.public_reqby_photo_id,
                }}
                size={32}
              />
              {CurrentTicket?.requestedby_id &&
                CurrentTicket?.requestedfor_id &&
                CurrentTicket?.requestedby_id !== CurrentTicket?.requestedfor_id && (
                  <UserAvatarGroupItem
                    user={{
                      id: CurrentTicket?.requestedfor_id,
                      name: CurrentTicket?.requested_for,
                      public_photo_id: CurrentTicket?.public_reqfor_photo_id,
                    }}
                    size={32}
                  />
                )}
            </AvatarGroup>
            <Badge
              shape="circular"
              size="large"
              appearance="filled"
              style={{
                backgroundColor: CurrentTicket?.type === "INC" ? "#FFA500" : "#28A745",
                fontSize: "18px",
                fontWeight: "bold",
                color: "#FFFFFF",
                width: "fit-content",
                margin: "0",
                padding: "4px 8px",
              }}
              color={CurrentTicket?.type === "INC" ? "#FFA500" : "#28A745"}
            >
              {CurrentTicket?.type}-{CurrentTicket?.id}
            </Badge>
            {isTitleEditing ? (
              <Input
                id="title"
                size="large"
                style={{ flex: 1, margin: 0, width: "100%" }}
                maxLength="255"
                placeholder={t("generic.title")}
                value={CurrentTicket?.title || ""}
                onChange={(e, data) => updateField("title", data.value)}
                onBlur={() => setIsTitleEditing(false)}
                autoFocus
                validationState={validationErrors.title ? "error" : undefined}
              />
            ) : (
              <>
                <div style={{ display: "flex", flex: 1, alignItems: "center", gap: "8px", minWidth: 0 }}>
                  <h2 style={{ margin: 0 }}>{CurrentTicket?.title}</h2>
                  {associatedCis && <CisBar editable={true} cis={associatedCis} onRemoveCi={handleRemoveCi} />}
                </div>
                <Button
                  appearance="subtle"
                  icon={<Edit24Regular />}
                  onClick={() => setIsTitleEditing(true)}
                  title={t("generic.edit", { defaultValue: "Edit" })}
                />
              </>
            )}
          </span>
        </header>
        <Toolbar style={{ margin: "00 0 0", gap: "0px" }}>
          <ToolbarButton
            icon={<Desktop24Regular />}
            onClick={() => setIsCiSelectorOpen(true)}
            title={t("generic.edit", { defaultValue: "Edit" })}
            style={{ padding: "4px 8px" }}
          >
            {t("nounPhrases.associateCi", { defaultValue: "Associate a CI" })}
          </ToolbarButton>
          {ticketData && (
            <>
              <ToolbarButton
                icon={<Note24Regular />}
                onClick={() => {
                  setShowEditor(true);
                  setShowMailEditor(false);
                }}
                style={{ padding: "4px 8px" }}
              >
                {t("nounPhrases.addNote", { defaultValue: "Add note" })}
              </ToolbarButton>
              <ToolbarButton
                icon={<Mail24Regular />}
                onClick={() => {
                  setMailProperties({
                    subject: `[#${CurrentTicket?.type}-${CurrentTicket?.id}] ${CurrentTicket?.title}`,
                  });
                  setShowMailEditor(true);
                  setShowEditor(false);
                }}
                style={{ padding: "4px 8px" }}
              >
                {t("nounPhrases.sendEmail", { defaultValue: "Send email" })}
              </ToolbarButton>
              <ToolbarButton onClick={() => refetchTicketUpdates()} icon={<ArrowClockwiseRegular />}>
                {t("generic.refresh", { defaultValue: "Refresh" })}
              </ToolbarButton>
            </>
          )}
          {/* Add more ToolbarButtons as needed */}
        </Toolbar>

        <ErrorMessagesBar errors={errors} onDismiss={() => setErrors([])} />
        <main>
          <div style={{ display: "flex", flexDirection: "row", height: "100%" }}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <TabList selectedValue="conversations" style={{ overflowY: "hidden", flexShrink: 0 }}>
                <Tab value="conversations">{t("generic.conversations", { defaultValue: "Conversations" })}</Tab>
              </TabList>
              <TicketEditorConversations
                conversationRef={conversationRef}
                newTicket={newTicket}
                CurrentTicket={CurrentTicket}
                updateField={updateField}
                attachmentsRef={attachmentsRef}
                ticketUpdatesData={ticketUpdatesData}
                ticketData={ticketData}
                t={t}
                setShowEditor={setShowEditor}
                setShowMailEditor={setShowMailEditor}
                //setEditorValue={setEditorValue}
                showEditor={showEditor}
                //editorValue={editorValue}
                updateLanguage={updateLanguage}
                setUpdateLanguage={setUpdateLanguage}
                hasScrollbar={hasScrollbar}
                showMailEditor={showMailEditor}
                mailProperties={mailProperties}
                setMailProperties={setMailProperties}
                onEmailReply={handleOnEmailReply}
              />
            </div>
            <div style={{ width: "50px" }} />
            <TicketEditorProperties
              validationErrors={validationErrors}
              ticketStatusesData={ticketStatusesData}
              CurrentTicket={CurrentTicket}
              updateField={updateField}
              agentsData={agentsData}
              impactOptions={impactOptions}
              priorityOptions={priorityOptions}
              categoriesData={categoriesData}
              subcategoriesData={subcategoriesData}
              itemsData={itemsData}
              openItems={openItems}
              setUserToggledAccordions={setUserToggledAccordions}
              setOpenItems={setOpenItems}
              accordionRef={accordionRef}
              ticketSourcesData={ticketSourcesData}
              usersData={usersData}
              sitesData={sitesData}
              t={t}
              styles={styles}
            />
          </div>
        </main>
        <div
          className="footerButtons"
          style={{
            flexShrink: 0,
            padding: "8px 12px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "15px",
          }}
        >
          <Button
            appearance="primary"
            icon={isCreating || isUpdating ? <Spinner size="tiny" /> : undefined}
            disabled={Object.keys(validationErrors).length > 0}
            onClick={() =>
              CurrentTicket?.id > 0
                ? updateTicketMutate({ ...CurrentTicket, cis: associatedCis.map((ci) => ci.id) })
                : createTicketMutate({
                    ...CurrentTicket,
                    cis: associatedCis.map((ci) => ci.id),
                    update: {
                      update_type: "note",
                      content: CurrentTicket.description,
                      language: CurrentTicket.language,
                    },
                  })
            }
          >
            {t(CurrentTicket?.id > 0 ? "generic.update" : "generic.create")}
          </Button>
          <Button appearance="secondary" onClick={handleClose}>
            {t("generic.cancel")}
          </Button>
        </div>
        {isCiSelectorOpen && (
          <CiSelector
            onSelect={handleOnCiSelect}
            isOpen={isCiSelectorOpen}
            onClose={() => setIsCiSelectorOpen(false)}
          />
        )}
      </div>
    );
}

export default TicketEditor;
